import {
  ViewGate,
  AccessGuard,
  TodaySealedTitleConnected,
  TodaySealedFooterConnected,
  WorkLogRedirectWindow,
  AdminAttendanceWindow,
} from './windowRegistryComponents';
import { TeamCapability } from '@aegislab/shared';
import {
  liveWindowSize,
  scenarioWindowSize,
  teaserWindowSize,
  type WindowConfig,
} from '@/shared/config';
import { useWindowStore } from '@/shared/model';
import { lazyWindow } from './lazyWindow';
import { buildCampaignBonusWindow } from './campaignBonusWindow';
import { buildUsageDashboardWindow } from './usageDashboardWindow';
// 배럴(@/features/bug-report)이 아니라 컴포넌트를 직접 집는다 — 배럴은 BugReportPage 를
// 재수출하므로 eager import 하면 lazyWindow 로 떼어낸 창 청크가 엔트리로 도로 딸려온다.
import { BugReportViewTabs } from '@/features/bug-report/ui/BugReportViewTabs/BugReportViewTabs';
import { BugReportMobileBack } from '@/features/bug-report/ui/BugReportMobileBack/BugReportMobileBack';
import { CreatorRelationsMobileBack } from '@/features/creator-relations/ui/CreatorRelationsMobileBack/CreatorRelationsMobileBack';
import { QuestMobileBack } from '@/pages/QuestsPage/ui/QuestMobileBack/QuestMobileBack';

// 창 모듈은 전부 lazyWindow 경유 — 맨 React.lazy 는 배포 직후 preload 실패를 빈 화면으로 흘린다.
// (근거·복구 경로는 lazyWindow.tsx, 회귀 가드는 windowRegistry.test.tsx)
const TeamSettingsPage = lazyWindow('팀 운영', () =>
  import('@/pages/TeamSettingsPage').then((m) => ({ default: m.TeamSettingsPage }))
);
const FinancePage = lazyWindow('포인트 정산', () =>
  import('@/pages/FinancePage').then((m) => ({ default: m.FinancePage }))
);
const AttendancePage = lazyWindow('출퇴근', () =>
  import('@/pages/AttendancePage').then((m) => ({ default: m.AttendancePage }))
);
const LeaderboardPage = lazyWindow('리더보드', () =>
  import('@/pages/LeaderboardPage').then((m) => ({ default: m.LeaderboardPage }))
);
const AdminAttendanceDashboardConnected = lazyWindow('근태 운영', () =>
  import('@/pages/AdminAttendancePage').then((m) => ({
    default: m.AdminAttendanceDashboardConnected,
  }))
);

const ChatPage = lazyWindow('채팅', () =>
  import('@/pages/ChatPage').then((m) => ({ default: m.ChatPage }))
);
const HrManagementPage = lazyWindow('인사 관리', () =>
  import('@/pages/HrManagementPage').then((m) => ({ default: m.HrManagementPage }))
);
const CampaignSettlementPage = lazyWindow('글로벌 정산', () =>
  import('@/pages/CampaignSettlementPage').then((m) => ({ default: m.CampaignSettlementPage }))
);
const DomesticSettlementPage = lazyWindow('국내 정산', () =>
  import('@/pages/DomesticSettlementPage').then((m) => ({ default: m.DomesticSettlementPage }))
);
const SystemAdminPage = lazyWindow('시스템 관리', () =>
  import('@/pages/SystemAdminPage').then((m) => ({ default: m.SystemAdminPage }))
);
const BUG_REPORT_TITLE = '제보 · 요청';
// 페이지 레이어 래퍼 — 목록·상세는 feature 가 그리고 "퀘스트로 전환" 모달만 페이지가 소유한다.
const BugReportPage = lazyWindow(BUG_REPORT_TITLE, () =>
  import('@/pages/BugReportWindow').then((m) => ({ default: m.BugReportWindow }))
);
const WikiWindow = lazyWindow('위키', () =>
  import('@/pages/WikiWindow').then((m) => ({ default: m.WikiWindow }))
);
const TodayPage = lazyWindow('오늘 일지', () =>
  import('@/pages/TodayPage').then((m) => ({ default: m.TodayPage }))
);
const ScenarioPage = lazyWindow('시나리오', () =>
  import('@/pages/ScenarioPage').then((m) => ({ default: m.ScenarioPage }))
);
const AdvertiserConsolePage = lazyWindow('성좌', () =>
  import('@/pages/AdvertiserConsolePage').then((m) => ({ default: m.AdvertiserConsolePage }))
);
const CeoConsolePage = lazyWindow('대표 콘솔', () =>
  import('@/pages/CeoConsolePage').then((m) => ({ default: m.CeoConsolePage }))
);
const QuestsPage = lazyWindow('퀘스트', () =>
  import('@/pages/QuestsPage').then((m) => ({ default: m.QuestsPage }))
);
const LoungePage = lazyWindow('라운지', () =>
  import('@/pages/LoungePage').then((m) => ({ default: m.LoungePage }))
);
// 관계 층은 페이지 껍데기 없이 feature 컨테이너가 창 본체다 — 페이지가 할 일(모달 소유·라우트)이
// 아직 없어 빈 래퍼만 늘어난다. 배럴을 타도 되는 것은 이 배럴이 pages 를 재수출하지 않아서다.
const CreatorListsWindow = lazyWindow('명단 내려받기', () =>
  import('@/features/creator-lists').then((m) => ({ default: m.CreatorListsWindow }))
);
const CreatorRelationsConsole = lazyWindow('크리에이터 관계', () =>
  import('@/features/creator-relations').then((m) => ({ default: m.CreatorRelationsConsole }))
);
const CreatorMailWindow = lazyWindow('메일 스레드', () =>
  import('@/pages/CreatorMailWindow').then((m) => ({ default: m.CreatorMailWindow }))
);

import { getViewState } from '@/shared/config';

/**
 * 피처 플래그 + capability 기반 윈도우 chrome 빌더.
 *
 * 모듈 init 시점에 `getViewState()` 를 캡처하면 `teamCapabilities` 가 비어 BETA_TESTER 우회가
 * 적용되지 않는다 — 콘텐츠는 ViewGate 런타임 평가로 live 가 되지만 chrome 은 teaser 값에 봉인.
 * 그래서 size/title 도 런타임 소비 시점에 함께 평가한다.
 */
/**
 * 창이 앉을 평면의 세로 — 프리셋의 하한을 재는 기준이다.
 *
 * 창 평면(`#window-plane`)을 먼저 본다. 창은 그 안에 앉으므로 헤더·독을 뺀 실제 높이가 여기다.
 * 평면이 아직 없으면(첫 렌더·테스트) 뷰포트를, 그것도 없으면 0 을 준다 — 0 이면 프리셋이
 * `standard` 하한으로 선다.
 */
function viewportHeight(): number {
  if (typeof document === 'undefined') return 0;
  const plane = document.getElementById('window-plane');
  if (plane && plane.clientHeight > 0) return plane.clientHeight;
  return typeof window === 'undefined' ? 0 : window.innerHeight;
}

export function buildWindowRegistry(teamCapabilities: TeamCapability[]): WindowConfig[] {
  const todayState = getViewState('today', teamCapabilities);
  // 미니게임(가챠 뽑기)·가챠 운영 윈도우는 폐기됐다. 'gamification' 플래그는 남아 있지만
  // OUTBREAK_TRIGGERED 알림 게이트(NOTIFICATION_FEATURE_MAP) 전용이고 윈도우와는 무관하다.
  const leaderboardState = getViewState('leaderboard', teamCapabilities);
  const scenariosState = getViewState('scenarios', teamCapabilities);
  const advertiserState = getViewState('advertiser-console', teamCapabilities);
  const ceoConsoleState = getViewState('ceo-console', teamCapabilities);
  const loungeState = getViewState('lounge', teamCapabilities);
  const wikiState = getViewState('wiki', teamCapabilities);

  return [
    {
      id: 'today',
      title: todayState !== 'live' ? '[DECRYPTING...]' : '오늘 일지',
      defaultX: 60,
      defaultY: 20,
      ...(todayState !== 'live' ? teaserWindowSize : liveWindowSize.text),
      // Daily Sealed Log motif chrome (live 상태에서만 적용 — decrypting 상태는 generic).
      // [Source: project_todaypage_daily_sealed_log_motif §Shell 구조 — D8 hybrid + window chrome variant 결정]
      ...(todayState === 'live' && {
        variant: 'dailySealedLog' as const,
        titleSlot: <TodaySealedTitleConnected />,
        footerSlot: <TodaySealedFooterConnected />,
      }),
      render: () => (
        <ViewGate viewId="today" viewLabel="오늘 일지">
          {/* 카드 안 '증빙 제출하러 가기'의 목적지 — DEV capability 면 개발 퀘스트 창(제품 축)으로. */}
          <TodayPage hasDevQuestWindow={teamCapabilities.includes(TeamCapability.DEV)} />
        </ViewGate>
      ),
    },
    {
      id: 'quests',
      title: '퀘스트',
      defaultX: 40,
      defaultY: 20,
      ...liveWindowSize.quests,
      mobileLeadingSlot: <QuestMobileBack scope="TEAM" />,
      render: () => (
        <QuestsPage
          scope="TEAM"
          onNavigateToWorkLog={(questId) =>
            useWindowStore.getState().openWindow('today', { questId })
          }
        />
      ),
    },
    {
      id: 'world-quests',
      title: '월드 퀘스트',
      defaultX: 80,
      defaultY: 40,
      ...liveWindowSize.standard,
      mobileLeadingSlot: <QuestMobileBack scope="GLOBAL" />,
      render: () => (
        <QuestsPage
          scope="GLOBAL"
          onNavigateToWorkLog={(questId) =>
            useWindowStore.getState().openWindow('today', { questId })
          }
        />
      ),
    },
    {
      // 개발 퀘스트 — 팀 퀘스트를 사내 제품 축으로 보는 세 번째 인스턴스.
      // scope 는 TEAM 그대로다. 퀘스트 종류가 늘어난 게 아니라 묶는 축만 다르다(퀘스트 911e467c).
      id: 'dev-quests',
      title: '개발 퀘스트',
      defaultX: 120,
      defaultY: 60,
      ...liveWindowSize.standard,
      mobileLeadingSlot: <QuestMobileBack scope="TEAM" />,
      render: () => (
        <QuestsPage
          scope="TEAM"
          spineAxis="product"
          onNavigateToWorkLog={(questId) =>
            useWindowStore.getState().openWindow('today', { questId })
          }
        />
      ),
    },
    {
      id: 'attendance',
      title: '출퇴근',
      defaultX: 200,
      defaultY: 30,
      ...liveWindowSize.attendance,
      render: () => <AttendancePage />,
    },
    {
      id: 'chat',
      title: '채팅',
      defaultX: 120,
      defaultY: 60,
      ...liveWindowSize.standard,
      render: () => <ChatPage />,
    },
    {
      id: 'work-log',
      title: '업무 일지',
      defaultX: 160,
      defaultY: 50,
      ...liveWindowSize.text,
      render: () => <WorkLogRedirectWindow />,
    },
    {
      id: 'team-settings',
      title: '팀 운영',
      defaultX: 180,
      defaultY: 40,
      ...liveWindowSize.wideAdmin,
      render: () => <TeamSettingsPage />,
    },
    {
      id: 'finance',
      title: '포인트 정산',
      defaultX: 220,
      defaultY: 30,
      ...liveWindowSize.wideAdmin,
      variant: 'operatorConsole' as const,
      render: () => (
        <AccessGuard view="finance" full>
          <FinancePage />
        </AccessGuard>
      ),
    },
    {
      id: 'leaderboard',
      title: leaderboardState !== 'live' ? '[DECRYPTING...]' : '리더보드',
      defaultX: 100,
      defaultY: 20,
      ...(leaderboardState !== 'live' ? teaserWindowSize : liveWindowSize.leaderboard),
      render: () => (
        <ViewGate viewId="leaderboard" viewLabel="리더보드">
          <LeaderboardPage />
        </ViewGate>
      ),
    },
    {
      id: 'admin-attendance',
      title: '근태 운영',
      defaultX: 140,
      defaultY: 50,
      ...liveWindowSize.standard,
      render: () => (
        <AdminAttendanceWindow>
          <AdminAttendanceDashboardConnected />
        </AdminAttendanceWindow>
      ),
    },
    {
      id: 'hr-management',
      title: '인사 관리',
      defaultX: 160,
      defaultY: 40,
      ...liveWindowSize.standard,
      render: () => (
        <AccessGuard view="hr-management" full>
          <HrManagementPage />
        </AccessGuard>
      ),
    },
    {
      id: 'campaign-settlement',
      title: '글로벌 정산',
      defaultX: 170,
      defaultY: 50,
      ...liveWindowSize.standard,
      render: () => (
        <AccessGuard view="campaign-settlement" full>
          <CampaignSettlementPage />
        </AccessGuard>
      ),
    },
    {
      id: 'domestic-settlement',
      title: '국내 정산',
      defaultX: 175,
      defaultY: 45,
      ...liveWindowSize.standard,
      render: () => (
        <AccessGuard view="domestic-settlement" full>
          <DomesticSettlementPage />
        </AccessGuard>
      ),
    },
    {
      id: 'system-admin',
      title: '시스템 관리',
      defaultX: 180,
      defaultY: 30,
      ...liveWindowSize.wideAdmin,
      variant: 'operatorConsole' as const,
      render: () => (
        <AccessGuard view="system-admin" full>
          <SystemAdminPage />
        </AccessGuard>
      ),
    },
    {
      id: 'bug-reports',
      title: BUG_REPORT_TITLE,
      // 뷰 토글(목록/통계)을 타이틀바로 올린다 — 본문에 있던 그 줄은 창 폭의 20% 만 쓰면서
      // 세로 45px 을 가져갔다. titleSlot 은 title 을 대체하므로 제목도 이 컴포넌트가 그린다.
      titleSlot: <BugReportViewTabs title={BUG_REPORT_TITLE} />,
      mobileLeadingSlot: <BugReportMobileBack />,
      defaultX: 120,
      defaultY: 50,
      ...liveWindowSize.wideAdmin,
      render: () => <BugReportPage />,
    },
    {
      id: 'scenarios',
      title: scenariosState !== 'live' ? '[DECRYPTING...]' : '시나리오',
      defaultX: 100,
      defaultY: 30,
      /*
        세로만 `standard` 에서 벗어난다 — 덮개 안에 작성기(캔버스 600 + 조작 260)가 서는데
        하한 600 에서는 캔버스 위쪽이 잘렸다(2026-09-22 dev 실측). 하한은 뷰포트의 70% 다.
      */
      ...(scenariosState !== 'live' ? teaserWindowSize : scenarioWindowSize(viewportHeight())),
      render: () => (
        <ViewGate viewId="scenarios" viewLabel="시나리오">
          <AccessGuard view="scenarios">
            <ScenarioPage />
          </AccessGuard>
        </ViewGate>
      ),
    },
    {
      id: 'advertiser-console',
      title: advertiserState !== 'live' ? '[DECRYPTING...]' : '성좌',
      defaultX: 140,
      defaultY: 40,
      ...(advertiserState !== 'live' ? teaserWindowSize : liveWindowSize.advertiser),
      render: () => (
        <ViewGate viewId="advertiser-console" viewLabel="성좌">
          <AccessGuard view="advertiser-console">
            <AdvertiserConsolePage />
          </AccessGuard>
        </ViewGate>
      ),
    },
    {
      /*
       * 크리에이터 관계 — CP3 결정 1: 창 하나. 담당 큐는 별도 창이 아니라 이 창의
       * "프리미엄 시트만" 토글 뷰다(담당이 빈 행이 곧 시딩 팀장 큐 — 승인 시안 §2.7).
       * 목록·상세 2열이라 창 크기는 advertiser 프리셋을 그대로 쓴다.
       */
      id: 'creator-relations',
      title: '크리에이터 관계',
      defaultX: 120,
      defaultY: 40,
      ...liveWindowSize.advertiser,
      mobileLeadingSlot: <CreatorRelationsMobileBack />,
      render: () => (
        <ViewGate viewId="creator-relations" viewLabel="크리에이터 관계">
          <AccessGuard view="creator-relations">
            <CreatorRelationsConsole />
          </AccessGuard>
        </ViewGate>
      ),
    },
    {
      /*
       * 명단 내려받기 (퀘스트 565e2df5) — 독·빠른 이동 「팀」 넷째. 시딩 팀 팀장 자격이 있어야 열리고
       * 가드가 자격 조회의 확인 중·실패를 자격 없음과 가른다(docs/design/ia/creator-lists-ia.md §1).
       * 새 명단 300px 과 내 명단 목록이 나란히 서는 1000×600(하한 900×500).
       */
      id: 'creator-lists',
      title: '명단 내려받기',
      defaultX: 140,
      defaultY: 50,
      ...liveWindowSize.wideAdmin,
      render: () => (
        <AccessGuard view="creator-lists">
          <CreatorListsWindow />
        </AccessGuard>
      ),
    },
    {
      /*
        메일 스레드 (퀘스트 d90b1656) — 덮개 스레드 패널의 「창으로 열기」 로만 열린다. 메뉴에 없고
        그 덮개가 참여팀 판정을 지난 자리라 뷰 게이트를 두지 않는다(9/19 작성기 창과 같다). 본문
        열람·발신은 서버가 다시 건다. 왼쪽 맥락 칸 252 와 스레드가 나란히 서는 폭이 880 이다.
      */
      id: 'creator-mail-thread',
      title: '메일 스레드',
      defaultX: 160,
      defaultY: 60,
      defaultWidth: 880,
      defaultHeight: 680,
      minWidth: 480,
      minHeight: 480,
      render: () => <CreatorMailWindow />,
    },
    {
      id: 'wiki',
      title: wikiState !== 'live' ? '[DECRYPTING...]' : '위키',
      defaultX: 90,
      defaultY: 30,
      ...(wikiState !== 'live' ? teaserWindowSize : liveWindowSize.wiki),
      render: () => (
        <ViewGate viewId="wiki" viewLabel="위키">
          <WikiWindow />
        </ViewGate>
      ),
    },
    {
      id: 'lounge',
      title: loungeState !== 'live' ? '[DECRYPTING...]' : '라운지',
      defaultX: 100,
      defaultY: 40,
      ...(loungeState !== 'live' ? teaserWindowSize : liveWindowSize.lounge),
      render: () => (
        <ViewGate viewId="lounge" viewLabel="라운지">
          <LoungePage />
        </ViewGate>
      ),
    },
    {
      id: 'ceo-console',
      title: ceoConsoleState !== 'live' ? '[DECRYPTING...]' : '대표 콘솔',
      defaultX: 160,
      defaultY: 30,
      ...(ceoConsoleState !== 'live' ? teaserWindowSize : liveWindowSize.ceoConsole),
      variant: 'operatorConsole' as const,
      render: () => (
        <ViewGate viewId="ceo-console" viewLabel="대표 콘솔">
          <AccessGuard view="ceo-console" full>
            <CeoConsolePage />
          </AccessGuard>
        </ViewGate>
      ),
    },
    buildUsageDashboardWindow(teamCapabilities),
    buildCampaignBonusWindow(teamCapabilities),
  ];
}

/**
 * Capability-independent window id list — store cleanup(pruneUnknownWindowIds) 등 caps 없이
 * id 만 필요한 호출자용. buildWindowRegistry 결과의 id 와 1:1 정합 유지.
 */
export const WINDOW_IDS = [
  'today',
  'quests',
  'world-quests',
  'dev-quests',
  'attendance',
  'chat',
  'work-log',
  'team-settings',
  'finance',
  'leaderboard',
  'admin-attendance',
  'hr-management',
  'campaign-settlement',
  'domestic-settlement',
  'system-admin',
  'bug-reports',
  'scenarios',
  'advertiser-console',
  'creator-relations',
  'creator-lists',
  'creator-mail-thread',
  'wiki',
  'lounge',
  'ceo-console',
  'usage-dashboard',
  'campaign-bonus',
] as const;
